import { z } from 'zod';
import { restricted } from './redaction.js';
export const SYSTEM_CARE_TASKS = [
    { code: 'BATHING', name: 'Bathing', category: 'ADL', activity: 'BATHING', requiresDetail: true },
    {
        code: 'SHOWERING',
        name: 'Showering',
        category: 'ADL',
        activity: 'BATHING',
        requiresDetail: true,
    },
    {
        code: 'DRESSING',
        name: 'Dressing',
        category: 'ADL',
        activity: 'DRESSING',
        requiresDetail: false,
    },
    {
        code: 'GROOMING',
        name: 'Grooming',
        category: 'ADL',
        activity: 'PERSONAL_HYGIENE',
        requiresDetail: false,
    },
    {
        code: 'ORAL_CARE',
        name: 'Oral care',
        category: 'ADL',
        activity: 'PERSONAL_HYGIENE',
        requiresDetail: false,
    },
    {
        code: 'TOILETING',
        name: 'Toileting',
        category: 'ADL',
        activity: 'TOILETING',
        requiresDetail: false,
    },
    {
        code: 'INCONTINENCE_CARE',
        name: 'Incontinence care',
        category: 'ADL',
        activity: 'TOILETING',
        requiresDetail: true,
    },
    {
        code: 'TRANSFERRING',
        name: 'Transferring',
        category: 'MOBILITY',
        activity: 'MOBILITY_TRANSFERS',
        requiresDetail: true,
    },
    {
        code: 'AMBULATION_ASSISTANCE',
        name: 'Ambulation assistance',
        category: 'MOBILITY',
        activity: 'MOBILITY_TRANSFERS',
        requiresDetail: false,
    },
    {
        code: 'POSITIONING',
        name: 'Positioning',
        category: 'MOBILITY',
        activity: 'MOBILITY_TRANSFERS',
        requiresDetail: true,
    },
    {
        code: 'FEEDING_ASSISTANCE',
        name: 'Feeding assistance',
        category: 'NUTRITION',
        activity: 'EATING_DRINKING',
        requiresDetail: true,
    },
    {
        code: 'MEAL_PREPARATION',
        name: 'Meal preparation',
        category: 'IADL',
        activity: 'MEAL_PREPARATION',
        requiresDetail: false,
    },
    {
        code: 'LIGHT_HOUSEKEEPING',
        name: 'Light housekeeping',
        category: 'IADL',
        activity: 'LIGHT_HOUSEKEEPING',
        requiresDetail: false,
    },
    {
        code: 'LAUNDRY',
        name: 'Laundry',
        category: 'IADL',
        activity: 'LAUNDRY',
        requiresDetail: false,
    },
    {
        code: 'SHOPPING_ERRANDS',
        name: 'Shopping and errands',
        category: 'IADL',
        activity: 'GROCERY_SHOPPING',
        requiresDetail: false,
    },
    {
        code: 'TRANSPORTATION',
        name: 'Transportation',
        category: 'IADL',
        activity: 'TRANSPORTATION',
        requiresDetail: true,
    },
    {
        code: 'MEDICATION_REMINDER',
        name: 'Medication reminder',
        category: 'MEDICATION_REMINDER',
        activity: 'MEDICATION_REMINDER',
        requiresDetail: true,
    },
    {
        code: 'VITAL_SIGNS_OBSERVATION',
        name: 'Vital signs (observation only)',
        category: 'SAFETY',
        activity: null,
        requiresDetail: true,
    },
    {
        code: 'COMPANIONSHIP',
        name: 'Companionship',
        category: 'SUPERVISION',
        activity: null,
        requiresDetail: false,
    },
    {
        code: 'SAFETY_SUPERVISION',
        name: 'Safety supervision',
        category: 'SUPERVISION',
        activity: null,
        requiresDetail: false,
    },
    {
        code: 'RESPITE',
        name: 'Respite',
        category: 'SUPERVISION',
        activity: null,
        requiresDetail: false,
    },
];
// ---------------------------------------------------------------------------
// The API (slice-03 session 10, §13.1)
// ---------------------------------------------------------------------------
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-06 — four digits, two, two.');
export const careTaskCategorySchema = z.enum([
    'ADL',
    'IADL',
    'SUPERVISION',
    'MEDICATION_REMINDER',
    'MOBILITY',
    'NUTRITION',
    'SAFETY',
    'OTHER',
]);
export const careTaskSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    category: careTaskCategorySchema,
    /** Needs instructions on the plan before the plan can start. */
    requiresDetail: z.boolean(),
    /** The platform's: may be used, never edited. */
    isSystem: z.boolean(),
});
export const careTaskListSchema = z.object({ tasks: z.array(careTaskSchema) });
/** An agency's own task, beside the system library. */
export const createCareTaskSchema = z.object({
    name: z.string().trim().min(1, 'Name the task.').max(120),
    description: z.string().trim().max(2_000).nullish(),
    category: careTaskCategorySchema,
    requiresDetail: z.boolean().default(false),
});
const goalSchema = z.object({
    text: z.string().trim().min(1, 'Write the goal, or remove it.').max(500),
    targetDate: isoDate.nullish(),
});
const planTaskInput = z.object({
    taskLibraryId: z.string().uuid(),
    /** 0–6, Sunday first. Empty: every visit. */
    daysOfWeek: z
        .array(z.number().int().min(0).max(6))
        .max(7)
        .refine((days) => new Set(days).size === days.length, { message: 'Each day once.' })
        .default([]),
    /** Null: every service the client receives. */
    serviceCodeId: z.string().uuid().nullish(),
    instructions: z.string().trim().max(2_000).nullish(),
    isRequired: z.boolean().default(true),
});
/**
 * A draft's whole content. Saving a draft replaces it: tasks are a list the
 * coordinator arranges, and a patch per task would be a dozen round trips to
 * reorder five lines.
 */
export const carePlanDraftSchema = z.object({
    effectiveFrom: isoDate,
    goals: z.array(goalSchema).max(20).default([]),
    precautions: z.string().trim().max(4_000).nullish(),
    tasks: z
        .array(planTaskInput)
        .max(60)
        .refine((tasks) => new Set(tasks.map((task) => `${task.taskLibraryId}|${task.serviceCodeId ?? '*'}`)).size ===
        tasks.length, { message: 'Each task once per service. Combine the days into one line instead.' }),
});
/** Start a draft — empty, or a revision copied from an activated version. */
export const createCarePlanDraftSchema = carePlanDraftSchema.extend({
    /** The version this revises. Its tasks, goals and precautions are the starting point. */
    revisesVersionId: z.string().uuid().nullish(),
});
export const carePlanTaskSchema = z.object({
    id: z.string().uuid(),
    taskLibraryId: z.string().uuid(),
    name: z.string(),
    category: careTaskCategorySchema,
    requiresDetail: z.boolean(),
    daysOfWeek: z.array(z.number().int()),
    serviceCodeId: z.string().uuid().nullable(),
    serviceCode: z.string().nullable(),
    /** Care plan instructions can name medication and conditions. */
    instructions: restricted(z.string().nullable(), 'client:read_phi'),
    isRequired: z.boolean(),
    sortOrder: z.number().int(),
});
export const versionStandingSchema = z.enum(['DRAFT', 'IN_FORCE', 'UPCOMING', 'SUPERSEDED']);
export const carePlanVersionSchema = z.object({
    id: z.string().uuid(),
    versionNumber: z.number().int(),
    standing: versionStandingSchema,
    effectiveFrom: isoDate,
    goals: z.array(z.object({ text: z.string(), targetDate: isoDate.nullable() })),
    /** Fall risk, allergies, DNR status — PHI. */
    precautions: restricted(z.string().nullable(), 'client:read_phi'),
    authoredByName: z.string().nullable(),
    approvedByName: z.string().nullable(),
    approvedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    tasks: z.array(carePlanTaskSchema),
});
export const carePlanSchema = z.object({
    clientId: z.string().uuid(),
    /** Null until a first draft is started. */
    carePlanId: z.string().uuid().nullable(),
    /** The agency's today, which the standings are about (R-1e). */
    today: isoDate,
    /** Newest first. */
    versions: z.array(carePlanVersionSchema),
});
/** Activation's answer: the plan, and how many booked visits took the new tasks. */
export const carePlanActivationSchema = z.object({
    plan: carePlanSchema,
    /** Future visits, not yet started, whose tasks were re-taken from this version. */
    visitsUpdated: z.number().int(),
});
//# sourceMappingURL=care-plan.js.map